import { nextTick } from 'vue'
import { ZMessage } from '@zoryn/components/message'
import { ZMessageBox } from '@zoryn/components/message-box'
import { getGridOptions, reloadGridData } from './query'
import {
  appendHeadIdToRow,
  hasPersistedRowId,
  isNewGridRow,
  prepareRowForCopy,
  prepareRowForInsert,
  prepareRowForUpdate,
  resolveModuleUrls,
} from '../api/api-convention'
import { resolveGridEditable } from '../column/meta'
import { activateRowEdit, hasActiveRowEdit, ROW_EDIT_BLOCKED_MESSAGE } from './edit'
import { getRowId } from '../utils/row-id'
import { notifyUnhandledRequestError } from '../utils/notify-request-failure'
import { resolveGridRequest } from '../utils/resolve-grid-request'
import type { DataGrid } from '../types'
import type { HttpRequest } from '@zoryn/network'

function collectRowsToSave($grid: DataGrid) {
  const { reactData } = $grid
  const { actived } = reactData.editStore
  if (reactData.editStr === 'new') {
    const newRows = reactData.tableData.filter((row) => !hasPersistedRowId(row))
    if (newRows.length) {
      return newRows
    }
  }
  if (actived.row) {
    return [actived.row]
  }
  if (reactData.currentRow) {
    return [reactData.currentRow]
  }
  return null
}

async function saveRowsWithLegacyUrl(
  request: HttpRequest,
  url: string,
  rows: Record<string, unknown>[],
  options: ReturnType<typeof getGridOptions>
) {
  for (const row of rows) {
    options.beforeUpsert?.(row)
    if (isNewGridRow(row)) {
      options.beforeInsert?.(row)
      await request.post({ url, params: prepareRowForInsert(row) })
    } else {
      options.beforeUpdate?.(row)
      await request.post({ url, params: prepareRowForUpdate(row) })
    }
  }
}

async function saveRowsWithConvention(
  request: HttpRequest,
  module: string,
  rows: Record<string, unknown>[],
  options: ReturnType<typeof getGridOptions>
) {
  const urls = resolveModuleUrls(module)
  const insertUrl = options.insertUrl || urls.insertRow
  const batchInsertUrl = options.batchInsertUrl || urls.batchInsert
  const updateUrl = options.updateUrl || urls.updateById

  const insertRows: Record<string, unknown>[] = []
  const updateRows: Record<string, unknown>[] = []

  for (const row of rows) {
    options.beforeUpsert?.(row)
    if (isNewGridRow(row)) {
      options.beforeInsert?.(row)
      insertRows.push(prepareRowForInsert(row))
    } else {
      options.beforeUpdate?.(row)
      updateRows.push(prepareRowForUpdate(row))
    }
  }

  if (insertRows.length === 1) {
    await request.post({ url: insertUrl, params: insertRows[0] })
  } else if (insertRows.length > 1) {
    await request.post({ url: batchInsertUrl, params: insertRows })
  }
  for (const row of updateRows) {
    await request.post({ url: updateUrl, params: row })
  }
}

async function beginNewRowEdit(
  $grid: DataGrid,
  record: Record<string, unknown>
) {
  const { reactData } = $grid
  getRowId(record)
  reactData.editStr = 'new'
  reactData.tableData.unshift(record)
  reactData.total += 1
  $grid.setCurrentRow(record)
  activateRowEdit($grid, record)
  await nextTick()
  $grid.scrollToTop?.()
  await nextTick()
  await $grid.focusRowFirstInput(record)
}

export function useToolbar($grid: DataGrid) {
  const { reactData } = $grid

  $grid.handleInsert = async () => {
    if (!resolveGridEditable($grid.props, $grid.options)) {
      return
    }
    if (hasActiveRowEdit($grid)) {
      ZMessage.warning(ROW_EDIT_BLOCKED_MESSAGE)
      return
    }
    const options = getGridOptions($grid)
    const record = { ...(options.defaultRow || {}) } as Record<string, unknown>
    appendHeadIdToRow(record, options)
    await beginNewRowEdit($grid, record)
  }

  $grid.handleCopy = async () => {
    if (!resolveGridEditable($grid.props, $grid.options)) {
      return
    }
    if (hasActiveRowEdit($grid)) {
      ZMessage.warning(ROW_EDIT_BLOCKED_MESSAGE)
      return
    }
    const options = getGridOptions($grid)
    const row = reactData.currentRow
    if (!row) {
      ZMessage.warning('请先选中要复制的数据')
      return
    }
    options.beforeCopy?.(row)
    const record = prepareRowForCopy(row, options.defaultRow)
    appendHeadIdToRow(record, options)
    await beginNewRowEdit($grid, record)
  }

  type SaveFollowUp = false | 'new' | 'copy'

  const saveData = async (followUp: SaveFollowUp = false) => {
    const options = getGridOptions($grid)
    const rows = collectRowsToSave($grid)
    if (!rows?.length) {
      ZMessage.warning('无可保存的数据')
      return
    }
    const rowSnapshot =
      followUp === 'copy' && rows.length === 1 ? { ...rows[0] } : null
    try {
      await $grid.validateRows(rows)
    } catch (e: unknown) {
      ZMessage.warning((e as Error)?.message ?? '请完善必填项')
      return
    }
    rows.forEach((row) => appendHeadIdToRow(row, options))
    try {
      reactData.loading = true
      const request = resolveGridRequest($grid)
      if (options.saveUrl) {
        await saveRowsWithLegacyUrl(request, options.saveUrl, rows, options)
      } else {
        await saveRowsWithConvention(request, options.module, rows, options)
      }
    } catch (e: unknown) {
      notifyUnhandledRequestError(e, '保存失败')
      throw e
    } finally {
      reactData.loading = false
    }
    try {
      reactData.editStr = ''
      reactData.editStore.actived = { row: null, field: null }
      reactData.validErrorMaps = {}
      await reloadGridData($grid)
      ZMessage.success('保存成功')
      if (followUp === 'new') {
        await $grid.handleInsert?.()
      } else if (followUp === 'copy' && rowSnapshot) {
        options.beforeCopy?.(rowSnapshot)
        const record = prepareRowForCopy(rowSnapshot, options.defaultRow)
        appendHeadIdToRow(record, options)
        await beginNewRowEdit($grid, record)
      }
    } catch {
      ZMessage.success('保存成功')
      ZMessage.warning('列表刷新失败，请手动刷新')
    }
  }

  $grid.saveData = () => saveData(false)
  $grid.saveDataAndNew = () => saveData('new')
  $grid.saveDataAndCopy = () => saveData('copy')

  $grid.handleDeleteRows = async () => {
    const options = getGridOptions($grid)
    const row = reactData.currentRow ?? reactData.editStore.actived.row
    if (!row) {
      ZMessage.warning('请先选中要删除的数据')
      return
    }
    try {
      await ZMessageBox.confirm('确定删除当前选中的记录吗?', '提示', {
        confirmButtonText: '确认',
        cancelButtonText: '取消',
        type: 'warning',
      })
    } catch {
      return
    }
    if (!hasPersistedRowId(row)) {
      reactData.tableData = reactData.tableData.filter((item) => item !== row)
      reactData.total = Math.max(0, reactData.total - 1)
      reactData.editStr = ''
      $grid.setCurrentRow(null)
      ZMessage.success('删除成功')
      return
    }
    try {
      reactData.loading = true
      options.beforeDelete?.(row)
      const request = resolveGridRequest($grid)
      if (options.deleteUrl) {
        await request.post({ url: options.deleteUrl, params: { ids: [row.id] } })
      } else {
        const urls = resolveModuleUrls(options.module)
        await request.post({
          url: options.deleteByIdUrl || urls.deleteById,
          params: { id: row.id },
        })
      }
    } catch (e: unknown) {
      notifyUnhandledRequestError(e, '删除失败')
      return
    } finally {
      reactData.loading = false
    }
    try {
      reactData.editStr = ''
      await reloadGridData($grid)
      ZMessage.success('删除成功')
    } catch {
      ZMessage.success('删除成功')
      ZMessage.warning('列表刷新失败，请手动刷新')
    }
  }
}
